import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import LoginPromptModal from '../../components/LoginPromptModal';
import PremiumUpgradeModal from '../../components/PremiumUpgradeModal';
import { useAuth } from '../../contexts/AuthContext';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR = 'linear-gradient(160deg, #fff5eb 0%, #fef3e8 40%, #e8f5f3 100%)';
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.15) translateY(-6px); }
  70% { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50% { transform: translateY(-8px) rotate(-3deg); }
`;
const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-8px); }
  30% { transform: translateX(8px); }
  45% { transform: translateX(-6px); }
  60% { transform: translateX(6px); }
`;
const popIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  70% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;
const correctPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6); }
  70% { box-shadow: 0 0 0 15px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
`;

// ─── Shapes Data ──────────────────────────────────────────────────────────────
interface ShapeType {
  id: string;
  nameId: string;
  path: string;
  viewBox: string;
}

const SHAPES: ShapeType[] = [
  { id: 'square', nameId: 'Persegi', viewBox: '0 0 100 100', path: 'M15,15 h70 v70 h-70 Z' },
  { id: 'circle', nameId: 'Lingkaran', viewBox: '0 0 100 100', path: 'M50,10 a40,40 0 1,1 0,80 a40,40 0 1,1 0,-80' },
  { id: 'triangle', nameId: 'Segitiga', viewBox: '0 0 100 100', path: 'M50,10 L90,90 L10,90 Z' },
  { id: 'diamond', nameId: 'Belah Ketupat', viewBox: '0 0 100 100', path: 'M50,5 L95,50 L50,95 L5,50 Z' },
  { id: 'star', nameId: 'Bintang', viewBox: '0 0 100 100', path: 'M50,5 L61,35 L95,35 L68,55 L79,90 L50,70 L21,90 L32,55 L5,35 L39,35 Z' },
  { id: 'pentagon', nameId: 'Pentagon', viewBox: '0 0 100 100', path: 'M50,5 L95,38 L77,90 L23,90 L5,38 Z' },
  { id: 'hexagon', nameId: 'Segi Enam', viewBox: '0 0 100 100', path: 'M50,5 L90,27 L90,73 L50,95 L10,73 L10,27 Z' },
  { id: 'heart', nameId: 'Hati', viewBox: '0 0 100 100', path: 'M50,88 C20,60 10,40 10,28 C10,12 25,5 40,15 C45,18 50,25 50,25 C50,25 55,18 60,15 C75,5 90,12 90,28 C90,40 80,60 50,88 Z' },
];

const COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// ─── Level Config ─────────────────────────────────────────────────────────────
interface LevelConfig {
  rounds: number;
  itemsPerRow: number;
  diffType: 'shape' | 'color' | 'size' | 'mixed';
  difficulty: 'easy' | 'medium' | 'hard';
}

const LEVELS: LevelConfig[] = [
  // Free levels (1-5)
  { rounds: 5, itemsPerRow: 4, diffType: 'shape', difficulty: 'easy' },
  { rounds: 6, itemsPerRow: 4, diffType: 'shape', difficulty: 'easy' },
  { rounds: 6, itemsPerRow: 4, diffType: 'shape', difficulty: 'medium' },
  { rounds: 7, itemsPerRow: 4, diffType: 'color', difficulty: 'easy' },
  { rounds: 7, itemsPerRow: 5, diffType: 'shape', difficulty: 'medium' },
  // Premium levels (6-10)
  { rounds: 8, itemsPerRow: 5, diffType: 'color', difficulty: 'medium' },
  { rounds: 8, itemsPerRow: 5, diffType: 'size', difficulty: 'easy' },
  { rounds: 9, itemsPerRow: 5, diffType: 'mixed', difficulty: 'medium' },
  { rounds: 9, itemsPerRow: 6, diffType: 'mixed', difficulty: 'hard' },
  { rounds: 10, itemsPerRow: 6, diffType: 'mixed', difficulty: 'hard' },
];

const MAX_LEVELS = LEVELS.length;
const DEFAULT_PREMIUM_START = 6;
const DEFAULT_LOGIN_START = 2;

// ─── Types ────────────────────────────────────────────────────────────────────
interface DisplayItem {
  id: string;
  shape: ShapeType;
  color: string;
  size: number; // 1 = normal, 0.7 = small, 1.3 = large
  isOdd: boolean;
}

// ─── Styled Components ────────────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center; overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px; margin: 0 auto; width: 100%;
  min-height: 100dvh; display: flex; flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatShape = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number; $color: string }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
  svg { width: 32px; height: 32px; fill: ${p => p.$color}; }
`;
const CharCard = styled.div`
  width: min(50vw, 180px); aspect-ratio: 1;
  display: flex; align-items: center; justify-content: center;
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
  position: relative;
`;
const MainShape = styled.div<{ $color: string }>`
  width: 100%; height: 100%;
  svg { width: 100%; height: 100%; fill: ${p => p.$color}; }
`;
const OddBadge = styled.div`
  position: absolute; top: -10px; right: -10px;
  background: #EF4444; color: white; font-size: 24px;
  width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; box-shadow: 0 4px 12px rgba(239,68,68,0.4);
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const ScoreBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 24px 16px; gap: 20px;
  max-width: 520px; margin: 0 auto; width: 100%;
`;
const LevelInfo = styled.div`
  font-size: 13px; color: #7a8a8a; text-align: center; font-weight: 600;
`;
const Instruction = styled.div`
  font-size: 18px; color: #1a2a2a; text-align: center; font-weight: 700;
  background: rgba(255,255,255,0.9); padding: 16px 24px; border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;
const ShapesGrid = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  max-width: 280px;
`;
const ShapeCard = styled.button<{ $correct: boolean | null; $shake: boolean; $size: number }>`
  aspect-ratio: 1;
  background: ${p => p.$correct === false ? 'rgba(239,68,68,0.1)' : p.$correct === true ? 'rgba(34,197,94,0.1)' : '#fff'} !important;
  border: 4px solid ${p => p.$correct === true ? '#22c55e' : p.$correct === false ? '#ef4444' : '#e2e8f0'} !important;
  border-radius: 16px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: ${p => p.$correct === false ? '0 0 0 4px rgba(239,68,68,0.2)' : p.$correct === true ? '0 0 0 4px rgba(34,197,94,0.2)' : '0 2px 8px rgba(0,0,0,0.08)'};
  padding: 8px;
  ${p => p.$correct === true && css`animation: ${correctPulse} 0.6s ease, ${popIn} 0.3s ease;`}
  ${p => p.$shake && css`animation: ${shakeAnim} 0.5s ease;`}
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(45,188,175,0.3);
  }
  &:disabled { cursor: default; }
  svg {
    width: ${p => p.$size * 80}%;
    height: ${p => p.$size * 80}%;
  }
`;
const RoundCounter = styled.div`
  font-size: 14px; color: #94a3b8; font-weight: 600;
`;

const ResultOverlay = styled.div`
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`;
const ResultCard = styled.div`
  background: #fff; border-radius: 28px; padding: 32px;
  text-align: center; max-width: 340px; width: 100%;
  animation: ${fadeInUp} 0.4s ease;
`;
const ResultEmoji = styled.div`font-size: 64px; margin-bottom: 16px;`;
const ResultTitle = styled.h2`font-size: 24px; font-weight: 900; color: #1a2a2a; margin: 0 0 8px;`;
const ResultDesc = styled.p`font-size: 14px; color: #7a8a8a; margin: 0 0 24px;`;
const ResultBtn = styled.button<{ $primary?: boolean }>`
  width: 100%; padding: 14px; border-radius: 99px; border: none;
  background: ${p => p.$primary ? PRIMARY_GR : '#f1f5f9'};
  color: ${p => p.$primary ? '#fff' : '#64748b'};
  font-size: 16px; font-weight: 700; cursor: pointer;
  font-family: ${FONT}; margin-bottom: 10px;
  transition: all 0.15s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: scale(0.97); }
  &:last-child { margin-bottom: 0; }
`;

// ─── Utility Functions ────────────────────────────────────────────────────────
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickRandom<T>(arr: T[], count: number = 1): T[] {
  return shuffle(arr).slice(0, count);
}

function generateRound(config: LevelConfig): DisplayItem[] {
  const { itemsPerRow, diffType, difficulty } = config;
  
  // Pick main shape and color
  const mainShape = pickRandom(SHAPES, 1)[0];
  const mainColor = pickRandom(COLORS, 1)[0];
  const mainSize = 1;
  
  // Determine what makes the odd one different
  let oddShape = mainShape;
  let oddColor = mainColor;
  let oddSize = mainSize;
  
  if (diffType === 'shape' || diffType === 'mixed') {
    // Pick a different shape
    const otherShapes = SHAPES.filter(s => s.id !== mainShape.id);
    if (difficulty === 'easy') {
      // Very different shapes (e.g., circle vs square)
      const easyPairs: Record<string, string[]> = {
        'circle': ['square', 'triangle', 'star'],
        'square': ['circle', 'triangle', 'star'],
        'triangle': ['circle', 'square', 'hexagon'],
        'star': ['circle', 'square', 'hexagon'],
        'pentagon': ['circle', 'square', 'triangle'],
        'hexagon': ['circle', 'square', 'triangle'],
        'diamond': ['circle', 'star', 'hexagon'],
        'heart': ['square', 'triangle', 'hexagon'],
      };
      const options = easyPairs[mainShape.id] || otherShapes.map(s => s.id);
      oddShape = SHAPES.find(s => s.id === pickRandom(options, 1)[0]) || otherShapes[0];
    } else {
      oddShape = pickRandom(otherShapes, 1)[0];
    }
  }
  
  if (diffType === 'color' || (diffType === 'mixed' && Math.random() > 0.5)) {
    oddShape = mainShape; // Same shape
    const otherColors = COLORS.filter(c => c !== mainColor);
    oddColor = pickRandom(otherColors, 1)[0];
  }
  
  if (diffType === 'size' || (diffType === 'mixed' && Math.random() > 0.7)) {
    oddShape = mainShape;
    oddColor = mainColor;
    oddSize = Math.random() > 0.5 ? 0.65 : 1.35;
  }
  
  // Create items
  const items: DisplayItem[] = [];
  const oddIndex = Math.floor(Math.random() * itemsPerRow);
  
  for (let i = 0; i < itemsPerRow; i++) {
    if (i === oddIndex) {
      items.push({
        id: `item-${i}`,
        shape: oddShape,
        color: oddColor,
        size: oddSize,
        isOdd: true,
      });
    } else {
      items.push({
        id: `item-${i}`,
        shape: mainShape,
        color: mainColor,
        size: mainSize,
        isOdd: false,
      });
    }
  }
  
  return items;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function OddOneOutGame() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [phase, setPhase] = useState<'intro' | 'playing' | 'levelComplete' | 'gameComplete'>('intro');
  const [level, setLevel] = useState(0);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [items, setItems] = useState<DisplayItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [result, setResult] = useState<'correct' | 'wrong' | null>(null);
  const [shakeId, setShakeId] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState<{ maxLevel: number; lastScore: number } | null>(null);
  const [gameSettings, setGameSettings] = useState({ loginLevel: DEFAULT_LOGIN_START, premiumLevel: DEFAULT_PREMIUM_START });
  
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  // Fetch settings and progress
  useEffect(() => {
    fetch('/api/games/odd-one-out')
      .then(res => res.json())
      .then(data => {
        if (data.loginRequiredLevel || data.premiumStartLevel) {
          setGameSettings({
            loginLevel: data.loginRequiredLevel || DEFAULT_LOGIN_START,
            premiumLevel: data.premiumStartLevel || DEFAULT_PREMIUM_START,
          });
        }
      })
      .catch(() => {});
    
    if (user) {
      fetch('/api/progress/odd-one-out', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.ok && data.progress) {
            setSavedProgress(data.progress);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const saveProgress = useCallback((completedLevel: number, currentScore: number) => {
    if (!user) return;
    fetch('/api/progress/odd-one-out', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ level: completedLevel, score: currentScore }),
    }).catch(() => {});
  }, [user]);

  const startRound = useCallback((lvl: number, rnd: number) => {
    // Reset all states first
    setSelectedId(null);
    setResult(null);
    setShakeId(null);
    setRound(rnd);
    
    // Generate new round with unique IDs
    const config = LEVELS[lvl];
    const newItems = generateRound(config).map((item, i) => ({
      ...item,
      id: `item-${lvl}-${rnd}-${i}`, // Unique ID per level+round
    }));
    setItems(newItems);
  }, []);

  const startGame = () => {
    setLevel(0);
    setRound(0);
    setScore(0);
    startRound(0, 0);
    setPhase('playing');
  };

  const handleNextLevel = () => {
    const next = level + 1;
    const nextLevelNum = next + 1;
    
    if (nextLevelNum >= gameSettings.loginLevel && !user) {
      setShowLoginModal(true);
      return;
    }
    
    if (nextLevelNum >= gameSettings.premiumLevel && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    
    if (next >= MAX_LEVELS) {
      playClap();
      setPhase('gameComplete');
    } else {
      setPhase('playing');
      setLevel(next);
      setRound(0);
      setTimeout(() => startRound(next, 0), 50);
    }
  };

  const handleGoHome = () => navigate('/');

  const handlePlayAgain = () => {
    setPhase('playing');
    setLevel(0);
    setRound(0);
    setScore(0);
    setTimeout(() => startRound(0, 0), 50);
  };

  const handleItemClick = (item: DisplayItem) => {
    if (result) return; // Already answered
    
    setSelectedId(item.id);
    
    if (item.isOdd) {
      // Correct!
      playCorrect();
      setResult('correct');
      setScore(s => s + 10);
      
      setTimeout(() => {
        const config = LEVELS[level];
        const nextRound = round + 1;
        
        if (nextRound >= config.rounds) {
          // Level complete
          playClap();
          saveProgress(level + 1, score + 10);
          setPhase('levelComplete');
        } else {
          // Next round
          startRound(level, nextRound);
        }
      }, 800);
    } else {
      // Wrong
      playWrong();
      setResult('wrong');
      setShakeId(item.id);
      setTimeout(() => {
        setSelectedId(null);
        setResult(null);
        setShakeId(null);
      }, 600);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  
  if (phase === 'intro') {
    return (
      <IntroOverlay>
        <IntroWrapper>
          <TopNav>
            <BackBtn onClick={() => navigate('/')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
          </TopNav>
          
          <CharArea>
            <FloatShape $top="15%" $left="10%" $delay={0} $color="#3B82F6">
              <svg viewBox={SHAPES[0].viewBox}><path d={SHAPES[0].path} /></svg>
            </FloatShape>
            <FloatShape $top="20%" $right="12%" $delay={1} $color="#3B82F6">
              <svg viewBox={SHAPES[0].viewBox}><path d={SHAPES[0].path} /></svg>
            </FloatShape>
            <FloatShape $bottom="25%" $left="15%" $delay={2} $color="#3B82F6">
              <svg viewBox={SHAPES[0].viewBox}><path d={SHAPES[0].path} /></svg>
            </FloatShape>
            <FloatShape $bottom="20%" $right="10%" $delay={3} $color="#EF4444">
              <svg viewBox={SHAPES[1].viewBox}><path d={SHAPES[1].path} /></svg>
            </FloatShape>
            
            <CharCard>
              <MainShape $color="#EF4444">
                <svg viewBox={SHAPES[1].viewBox}><path d={SHAPES[1].path} /></svg>
              </MainShape>
              <OddBadge>?</OddBadge>
            </CharCard>
          </CharArea>
          
          <InfoCard>
            <Handle />
            <IntroTitle>Temukan yang <span>Berbeda!</span></IntroTitle>
            <IntroDesc>
              Perhatikan dengan teliti! Temukan bentuk yang berbeda dari yang lainnya. 👀
            </IntroDesc>
            {savedProgress && savedProgress.maxLevel > 0 && (
              <IntroDesc style={{ color: PRIMARY, fontWeight: 600 }}>
                📊 Progress: Level {savedProgress.maxLevel} • Skor: {savedProgress.lastScore}
              </IntroDesc>
            )}
            <StartBtn onClick={startGame}>
              {savedProgress && savedProgress.maxLevel > 0 ? 'Main dari Awal' : 'Mulai Main'}
            </StartBtn>
            {savedProgress && savedProgress.maxLevel > 0 && savedProgress.maxLevel < MAX_LEVELS && (
              <StartBtn 
                onClick={() => {
                  const continueLevel = savedProgress.maxLevel;
                  const continueLevelNum = continueLevel + 1;
                  
                  if (continueLevelNum >= gameSettings.loginLevel && !user) {
                    setShowLoginModal(true);
                    return;
                  }
                  if (continueLevelNum >= gameSettings.premiumLevel && !isPremium) {
                    setShowPremiumModal(true);
                    return;
                  }
                  
                  setLevel(continueLevel);
                  setScore(savedProgress.lastScore);
                  setRound(0);
                  startRound(continueLevel, 0);
                  setPhase('playing');
                }}
                style={{ marginTop: 10, background: '#f1f5f9', color: '#475569', boxShadow: 'none' }}
              >
                Lanjutkan Level {savedProgress.maxLevel + 1}
              </StartBtn>
            )}
          </InfoCard>
        </IntroWrapper>
        
        {showLoginModal && (
          <LoginPromptModal
            onLogin={() => { setShowLoginModal(false); navigate('/profile'); }}
            onClose={() => setShowLoginModal(false)}
            subtitle={<>Login untuk melanjutkan ke level berikutnya!</>}
          />
        )}
        {showPremiumModal && (
          <PremiumUpgradeModal
            isOpen={showPremiumModal}
            onClose={() => setShowPremiumModal(false)}
            featureName="Level Premium"
          />
        )}
      </IntroOverlay>
    );
  }

  const config = LEVELS[level];

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => navigate('/')}>
            <span className="material-symbols-outlined">arrow_back</span>
          </BackBtn>
          <GameTitle>Temukan yang Berbeda</GameTitle>
        </NavLeft>
        <ScoreBadge>⭐ {score}</ScoreBadge>
      </GameNav>
      
      <GameBody>
        <LevelInfo>Level {level + 1} dari {MAX_LEVELS}</LevelInfo>
        <RoundCounter>Soal {round + 1} dari {config.rounds}</RoundCounter>
        
        <Instruction>👆 Tap bentuk yang berbeda!</Instruction>
        
        <ShapesGrid $count={config.itemsPerRow} key={`grid-${level}-${round}`}>
          {items.map(item => (
            <ShapeCard
              key={item.id}
              $correct={selectedId === item.id ? (result === 'correct' ? true : result === 'wrong' ? false : null) : null}
              $shake={shakeId === item.id}
              $size={item.size}
              onClick={() => handleItemClick(item)}
              disabled={result === 'correct'}
            >
              <svg viewBox={item.shape.viewBox}>
                <path d={item.shape.path} fill={item.color} />
              </svg>
            </ShapeCard>
          ))}
        </ShapesGrid>
      </GameBody>
      
      {phase === 'levelComplete' && !showLoginModal && !showPremiumModal && (
        <ResultOverlay>
          <ResultCard>
            <ResultEmoji>🎉</ResultEmoji>
            <ResultTitle>Level {level + 1} Selesai!</ResultTitle>
            <ResultDesc>Hebat! Matamu sangat jeli!</ResultDesc>
            <ResultBtn $primary onClick={handleNextLevel}>
              {level + 1 >= MAX_LEVELS ? 'Lihat Hasil' : 'Level Berikutnya'}
            </ResultBtn>
            <ResultBtn onClick={handleGoHome}>Kembali ke Menu</ResultBtn>
          </ResultCard>
        </ResultOverlay>
      )}
      
      {phase === 'gameComplete' && (
        <ResultOverlay>
          <ResultCard>
            <ResultEmoji>🏆</ResultEmoji>
            <ResultTitle>Luar Biasa!</ResultTitle>
            <ResultDesc>Kamu menyelesaikan semua level dengan skor {score} poin!</ResultDesc>
            <ResultBtn $primary onClick={handlePlayAgain}>Main Lagi</ResultBtn>
            <ResultBtn onClick={handleGoHome}>Kembali ke Menu</ResultBtn>
          </ResultCard>
        </ResultOverlay>
      )}
      
      {showLoginModal && (
        <LoginPromptModal
          onLogin={() => { setShowLoginModal(false); navigate('/profile'); }}
          onClose={() => setShowLoginModal(false)}
          subtitle={<>Login untuk melanjutkan ke level berikutnya!</>}
        />
      )}
      {showPremiumModal && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureName="Level Premium"
        />
      )}
    </GameWrap>
  );
}
